import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { produtosApi, GetProdutosPaginadoParams } from "../../hooks/produtosApi";
import { CatalogoUnico } from "../../types/Catalogo";
import { boolean } from "../../services/Functions";
import { Categoria as CategoriaAPI } from "../../types/Categoria";
import { FiltrosProdutos, Produto, TagDestaque } from "../../types/Produto";
import { tagsApi } from "../../hooks/tagsApi";
import { CatalogoContext } from "../Catalogo/CatalogoContext";
import { ProdutosContext } from "./ProdutosContext";

// Tipo para as categorias transformadas para uso nos selects
type CategoriaOption = { name: string; value: string };

interface ProdutosProviderProps {
  children: JSX.Element;
  tipoFiltro: "listagem-padrao" | "listagem-catalogo" | "listagem-promocao";
  idsPromocaoEditando?: number[];
}

const LIMIT = 25;

export const ProdutosProvider: React.FC<ProdutosProviderProps> = ({
  children,
  tipoFiltro,
  idsPromocaoEditando,
}) => {
  const apiProdutos = produtosApi();
  const context = useContext(CatalogoContext);
  const catalogoContext = tipoFiltro !== "listagem-padrao" ? context : null;
  const [catalogo, setCatalogo] = useState<CatalogoUnico | null>(catalogoContext?.catalogo || null);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newProductModal, setNewProductModal] = useState(false);

  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(0);
  const fetchingRef = useRef(false);
  const initialFetchDoneRef = useRef(false); // Rastreia se o primeiro fetch já foi feito

  // Função para salvar os filtros e filtroPesquisaAux no localStorage
  const saveToLocalStorage = (
    filtros: FiltrosProdutos,
    filtroPesquisaAux: string
  ) => {
    const storageKey =
      tipoFiltro === "listagem-catalogo" ? "produtosSettingsCatalogo"
      : tipoFiltro === "listagem-promocao" ? "produtosSettingsPromocao"
      : "produtosSettingsListagem";
    // No catálogo, não salvar filtros de preço para sempre começar do 0 ao maior valor
    const filtrosToSave = tipoFiltro === "listagem-catalogo"
      ? { ...filtros, filtroPrecoMin: undefined, filtroPrecoMax: undefined }
      : filtros;
    const dataToSave = { filtros: filtrosToSave, filtroPesquisaAux };
    localStorage.setItem(storageKey, JSON.stringify(dataToSave));
  };

  // Função para recuperar os filtros e filtroPesquisaAux do localStorage
  const getFromLocalStorage = () => {
    const storageKey =
      tipoFiltro === "listagem-catalogo" ? "produtosSettingsCatalogo"
      : tipoFiltro === "listagem-promocao" ? "produtosSettingsPromocao"
      : "produtosSettingsListagem";
    const storedData = localStorage.getItem(storageKey);
    return storedData ? JSON.parse(storedData) : null;
  };

  // Estado inicial dos filtros e filtroPesquisaAux
  const localStorageData = getFromLocalStorage();

  const [filtros, setFiltros] = useState<FiltrosProdutos>(() => {
    const defaultFiltros = {
      filtroPesquisa: "",
      filtroCategoria: "0",
      filtroAtivos: "1",
      filtroEstoque: "todos",
      filtroVisualizacao: "todos",
      filtroOrdenacao: "referencia-asc",
      filtroPrecoMin: 0,
      filtroPrecoMax: 100,
      filtroEstoqueMin: 0,
      filtroEstoqueMax: 9999,
      filtroTag: "todas",
      filtroPromocao: "todos",
    };
    if (!localStorageData?.filtros) {
      // No catálogo, usar valor alto para não restringir até o range real ser carregado
      if (tipoFiltro === "listagem-catalogo") {
        return { ...defaultFiltros, filtroPrecoMax: 999999 };
      }
      return defaultFiltros;
    }
    // No catálogo, sempre resetar filtros de preço para começar do 0 ao maior valor
    if (tipoFiltro === "listagem-catalogo") {
      return { ...localStorageData.filtros, filtroPrecoMin: 0, filtroPrecoMax: 999999 };
    }
    return localStorageData.filtros;
  });

  const [tagsDestaque, setTagsDestaque] = useState<TagDestaque[]>([]);

  const [filtroPesquisaAux, setFiltroPesquisaAux] = useState<string>(() => {
    return localStorageData?.filtroPesquisaAux || "";
  });

  const [categorias, setCategorias] = useState<CategoriaOption[]>([]);
  const optionsAtivos = [
    { name: "Ativos", value: "1" },
    { name: "Inativos", value: "0" },
  ];
  const optionsEstoque = [
    { name: "Todos estoques", value: "todos" },
    { name: "Com estoque", value: "1" },
    { name: "Sem estoque | Produto", value: "0" },
    { name: "Sem estoque | Variação", value: "variacao-zerada" },
    { name: "Sem controle de estoque", value: "-1" },
    { name: "Personalizado", value: "personalizado" },
  ];

  const optionsVisualizacao = [
    { name: "Todos produtos", value: "todos" },
    { name: "Produtos adicionados", value: "adicionados" },
    { name: "Produtos não adicionados", value: "nao-adicionados" },
    { name: "Produtos com foto", value: "com-foto" },
    { name: "Produtos sem foto", value: "sem-foto" },
  ];

  const optionsOrdenacao = [
    { name: "Nome A-Z", value: "referencia-asc" },
    { name: "Nome Z-A", value: "referencia-desc" },
    { name: "Maior estoque", value: "estoque-desc" },
    { name: "Menor estoque", value: "estoque-asc" },
    { name: "Mais novos", value: "data-asc" },
    { name: "Mais antigos", value: "data-desc" },
    { name: "Maior preço", value: "preco-desc" },
    { name: "Menor preço", value: "preco-asc" },
  ];

  const optionsTags = useMemo(() => {
    return [
      { name: "Todas as tags", value: "todas" },
      ...tagsDestaque
        .filter(tag => tag.ativo === 1)
        .map(tag => ({
          name: tag.nome_tag,
          value: tag.id.toString(),
        })),
    ];
  }, [tagsDestaque]);

  const getTags = async () => {
    try {
      const tags = await tagsApi().getTags();
      setTagsDestaque(tags);
    } catch (error) {
      console.error("Erro ao carregar tags:", error);
    }
  };

  const getCategorias = async () => {
    const categoriasData = await apiProdutos.getCategorias();

    const pais = categoriasData.filter((c: CategoriaAPI) => Number(c.id_pasta_pai) === 0).sort((a: CategoriaAPI, b: CategoriaAPI) => a.descricao.localeCompare(b.descricao));

    const options: { name: string; value: string }[] = [
      { name: "Todas as categorias", value: "0" },
    ];

    for (const pai of pais) {
      options.push({ name: pai.descricao, value: pai.id });

      const subs = categoriasData.filter((c: CategoriaAPI) => String(c.id_pasta_pai) === String(pai.id)).sort((a: CategoriaAPI, b: CategoriaAPI) => a.descricao.localeCompare(b.descricao));

      for (const sub of subs) {
        options.push({ name: `${pai.descricao} / ${sub.descricao}`, value: sub.id });
      }
    }

    setCategorias(options);
  };

  // Monta os parâmetros para a API de listagem
  const buildParams = useCallback((offset: number): GetProdutosPaginadoParams => {
    if (tipoFiltro === "listagem-promocao") {
      const params: GetProdutosPaginadoParams = {
        search: filtros.filtroPesquisa,
        categoria: filtros.filtroCategoria,
        ativos: "1",
        limit: LIMIT,
        offset,
      };
      if (catalogo) {
        params.id_tabela = catalogo.id_tabela;
        params.id_catalogo = catalogo.id;
        params.promocao = filtros.filtroPromocao;
        if (idsPromocaoEditando?.length) {
          params.ids_promocao = idsPromocaoEditando;
        }
        // Só os produtos do catálogo em edição (lista vazia não pode liberar todos)
        if (!boolean(catalogo.configuracoes.adicionarTodosProdutos)) {
          params.ids_catalogo = catalogo.produtos.length ? catalogo.produtos : [0];
        }
      }
      return params;
    }

    const params: GetProdutosPaginadoParams = {
      search: filtros.filtroPesquisa,
      categoria: filtros.filtroCategoria,
      ativos: tipoFiltro === "listagem-catalogo" ? "1" : filtros.filtroAtivos,
      estoque: filtros.filtroEstoque,
      estoque_min: filtros.filtroEstoqueMin,
      estoque_max: filtros.filtroEstoqueMax,
      tag: filtros.filtroTag,
      order: filtros.filtroOrdenacao,
      limit: LIMIT,
      offset,
    };

    // Parâmetros adicionais para catálogo
    if (tipoFiltro === "listagem-catalogo" && catalogo) {
      params.id_tabela = catalogo.id_tabela;
      params.visualizacao = filtros.filtroVisualizacao;
      params.preco_min = filtros.filtroPrecoMin;
      params.preco_max = filtros.filtroPrecoMax;
      params.id_catalogo = catalogo.id;
      params.promocao = filtros.filtroPromocao;
    }

    return params;
  }, [filtros, tipoFiltro, catalogo, idsPromocaoEditando]);

  // Busca produtos (reset = true reinicia a lista)
  const fetchProdutos = useCallback(async (reset: boolean = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    if (reset) {
      setLoading(true);
      pageRef.current = 0;
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    const offset = reset ? 0 : pageRef.current * LIMIT;
    const params = buildParams(offset);

    try {
      const novosProdutos = await apiProdutos.getProdutosPaginado(params);

      if (novosProdutos.length < LIMIT) {
        setHasMore(false);
      }

      if (reset) {
        setProdutos(novosProdutos);
      } else {
        setProdutos(prev => [...prev, ...novosProdutos]);
      }

      pageRef.current += 1;
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [buildParams, apiProdutos]);

  // Busca mais produtos (infinite scroll)
  const fetchMore = useCallback(async () => {
    if (!hasMore || loading || loadingMore) return;
    await fetchProdutos(false);
  }, [hasMore, loading, loadingMore, fetchProdutos]);

  // Reinicia e busca novamente (quando filtros mudam)
  const resetAndFetch = useCallback(async () => {
    await fetchProdutos(true);
  }, [fetchProdutos]);

  // Init - usado pelo catálogo quando aplica filtros
  const init = useCallback(async () => {
    initialFetchDoneRef.current = true;
    await fetchProdutos(true);
  }, [fetchProdutos]);

  // Quando filtros mudam, resetar e buscar
  useEffect(() => {
    // Para listagem-padrao: sempre busca automaticamente
    // Para listagem-catalogo: só busca após o primeiro "Aplicar filtros"
    if (tipoFiltro === "listagem-padrao" || initialFetchDoneRef.current) {
      fetchProdutos(true);
    } else {
      // Se não vai buscar, garante que loading seja false
      setLoading(false);
    }
  }, [
    filtros.filtroPesquisa,
    filtros.filtroCategoria,
    filtros.filtroAtivos,
    filtros.filtroEstoque,
    filtros.filtroEstoqueMin,
    filtros.filtroEstoqueMax,
    filtros.filtroTag,
    filtros.filtroOrdenacao,
    filtros.filtroVisualizacao,
    filtros.filtroPrecoMin,
    filtros.filtroPrecoMax,
    filtros.filtroPromocao,
  ]);

  // Inicialização
  useEffect(() => {
    getCategorias();
    getTags();
  }, []);

  // Salvar filtros no localStorage
  useEffect(() => {
    saveToLocalStorage(filtros, filtroPesquisaAux);
  }, [filtros, filtroPesquisaAux]);

  // Sincronizar catálogo do context
  useEffect(() => {
    if (catalogoContext?.catalogo) {
      setCatalogo(catalogoContext.catalogo);
    }
  }, [catalogoContext?.catalogo]);

  return useMemo(() => {
    return (
      <ProdutosContext.Provider
        value={{
          loading,
          setLoading,
          newProductModal,
          setNewProductModal,
          filtros,
          setFiltros,
          produtos,
          // produtosFiltrados agora é o mesmo que produtos (filtro é no backend)
          produtosFiltrados: produtos,
          filtroPesquisaAux,
          setFiltroPesquisaAux,
          optionsVisualizacao,
          optionsOrdenacao,
          categorias,
          optionsAtivos,
          optionsEstoque,
          optionsTags,
          init,
          // Novos campos para paginação
          hasMore,
          fetchMore,
          resetAndFetch,
          loadingMore,
        }}
      >
        {children}
      </ProdutosContext.Provider>
    );
  }, [
    loading,
    loadingMore,
    newProductModal,
    filtros,
    produtos,
    filtroPesquisaAux,
    categorias,
    catalogo,
    hasMore,
    fetchMore,
    resetAndFetch,
    optionsTags,
  ]);
};
